import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  FiX, FiClock, FiCheckCircle, FiXCircle, FiMinusCircle,
  FiEdit2, FiSave, FiRefreshCw, FiAlertTriangle, FiUser,
  FiMail, FiPhone, FiLinkedin, FiMessageSquare, FiCalendar,
} from 'react-icons/fi';
import { API_URL, getValidToken } from '../../../utils/authUtils';

/**
 * RelationshipMemoryViewer - Display complete relationship history for an executive target
 *
 * Shows:
 * - Receptivity score with visual gauge
 * - Cooldown status with countdown
 * - Engagement history breakdown
 * - Editable preferences
 * - Chronological interaction timeline
 *
 * Spec Reference: Phase 10 - Staff UI Polish
 */

const RelationshipMemoryViewer = ({
  execPersonId,
  execPersonName,
  onClose,
  onUpdate,
}) => {
  // State
  const [memory, setMemory] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [cooldownStatus, setCooldownStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit mode state
  const [editingPreferences, setEditingPreferences] = useState(false);
  const [editedPreferences, setEditedPreferences] = useState({});
  const [saving, setSaving] = useState(false);

  // Helper to extract error message from API response
  const getErrorMessage = (err, defaultMsg) => {
    if (err.response?.data) {
      const detail = err.response.data.detail;
      if (typeof detail === 'string') {
        return detail;
      } else if (Array.isArray(detail)) {
        return detail.map(e => e.msg || e.message || 'Error').join(', ');
      } else if (detail?.msg) {
        return detail.msg;
      }
    }
    return defaultMsg;
  };

  // Fetch relationship memory
  const fetchMemory = useCallback(async () => {
    if (!execPersonId) return;

    setLoading(true);
    setError(null);

    try {
      const token = getValidToken();
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch memory and cooldown status in parallel
      const [memoryRes, cooldownRes] = await Promise.all([
        axios.get(`${API_URL}/api/staff/brokerage/executives/${execPersonId}/memory`, { headers }),
        axios.get(`${API_URL}/api/staff/brokerage/executives/${execPersonId}/cooldown-check`, { headers }),
      ]);

      setMemory(memoryRes.data);
      setCooldownStatus(cooldownRes.data);

      // Initialize edit form with current values
      setEditedPreferences({
        preferred_contact_channel: memoryRes.data.preferred_contact_channel || 'email',
        preferred_cadence: memoryRes.data.preferred_cadence || 'quarterly',
        open_to_mentorship: memoryRes.data.open_to_mentorship ?? true,
        open_to_board_inquiries: memoryRes.data.open_to_board_inquiries ?? true,
        notes: memoryRes.data.notes || '',
      });

    } catch (err) {
      console.error('Error fetching relationship memory:', err);
      setError(getErrorMessage(err, 'Failed to load relationship memory'));
    } finally {
      setLoading(false);
    }
  }, [execPersonId]);

  // Fetch interaction timeline
  const fetchTimeline = useCallback(async () => {
    if (!execPersonId) return;

    try {
      const token = getValidToken();
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch audit log entries for this person
      const response = await axios.get(
        `${API_URL}/api/staff/brokerage/audit?entity_type=relationship_memory&target_person_id=${execPersonId}&limit=50`,
        { headers }
      );

      setTimeline(response.data.entries || []);
    } catch (err) {
      console.error('Error fetching timeline:', err);
      // Non-critical - don't show error
    }
  }, [execPersonId]);

  useEffect(() => {
    fetchMemory();
    fetchTimeline();
  }, [fetchMemory, fetchTimeline]);

  // Save preferences
  const handleSavePreferences = async () => {
    setSaving(true);

    try {
      const token = getValidToken();
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      await axios.patch(
        `${API_URL}/api/staff/brokerage/executives/${execPersonId}/memory`,
        editedPreferences,
        { headers }
      );

      // Refresh data
      await fetchMemory();
      setEditingPreferences(false);

      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error('Error saving preferences:', err);
      setError(getErrorMessage(err, 'Failed to save preferences'));
    } finally {
      setSaving(false);
    }
  };

  // Clear do-not-contact (staff only)
  const handleClearDoNotContact = async () => {
    if (!window.confirm('Are you sure you want to clear the do-not-contact flag? The person will be contactable again.')) {
      return;
    }

    setSaving(true);

    try {
      const token = getValidToken();
      const headers = { Authorization: `Bearer ${token}` };

      await axios.delete(
        `${API_URL}/api/staff/brokerage/executives/${execPersonId}/do-not-contact`,
        { headers }
      );

      await fetchMemory();

      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error('Error clearing do-not-contact:', err);
      setError(getErrorMessage(err, 'Failed to clear do-not-contact'));
    } finally {
      setSaving(false);
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Calculate days until cooldown ends
  const getDaysUntilCooldownEnds = () => {
    if (!cooldownStatus?.cooldown_until) return null;
    const now = new Date();
    const cooldownEnd = new Date(cooldownStatus.cooldown_until);
    const diffMs = cooldownEnd - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Receptivity gauge color
  const getReceptivityColor = (score) => {
    if (score >= 0.7) return 'bg-green-500';
    if (score >= 0.4) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Channel icon
  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'email': return <FiMail className="w-4 h-4" />;
      case 'linkedin': return <FiLinkedin className="w-4 h-4" />;
      case 'phone': return <FiPhone className="w-4 h-4" />;
      default: return <FiMessageSquare className="w-4 h-4" />;
    }
  };

  // Outcome icon
  const getOutcomeIcon = (outcome) => {
    switch (outcome) {
      case 'ACCEPTED':
      case 'positive':
        return <FiCheckCircle className="w-4 h-4 text-green-500" />;
      case 'DECLINED':
      case 'negative':
        return <FiXCircle className="w-4 h-4 text-red-500" />;
      case 'NO_RESPONSE':
      case 'no_response':
        return <FiMinusCircle className="w-4 h-4 text-gray-400" />;
      default:
        return <FiClock className="w-4 h-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 max-w-4xl w-full mx-4">
          <div className="flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            <span className="ml-3 text-gray-600 dark:text-gray-300">Loading relationship memory...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
              <FiUser className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Relationship Memory
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {execPersonName || 'Executive Profile'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FiX className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
              <FiAlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <span className="text-red-800 dark:text-red-200">{error}</span>
            </div>
          )}

          {/* No data state */}
          {!memory && !error && (
            <div className="p-8 text-center">
              <FiUser className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No relationship memory found for this executive.</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                Memory records are created after the first outreach attempt.
              </p>
            </div>
          )}

          {/* Stats Cards - Only show if memory exists */}
          {memory && (
          <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Receptivity Score */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                Receptivity Score
              </h3>
              {/* Check if there are actual interactions to calculate from */}
              {(memory.total_requests_received || 0) > 0 ? (
                <>
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                    {memory.receptivity_score != null
                      ? (memory.receptivity_score * 100).toFixed(0) + '%'
                      : 'N/A'}
                  </div>
                  <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getReceptivityColor(memory.receptivity_score || 0)} transition-all`}
                      style={{ width: `${(memory.receptivity_score || 0) * 100}%` }}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="text-lg font-medium text-gray-400 dark:text-gray-500 mb-2">
                    No data yet
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    Score will be calculated after first outreach
                  </p>
                </>
              )}
            </div>

            {/* Cooldown Status */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                Cooldown Status
              </h3>
              {cooldownStatus?.is_on_cooldown ? (
                <div>
                  <div className="text-lg font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-2">
                    <FiClock className="w-5 h-5" />
                    On Cooldown
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {getDaysUntilCooldownEnds()} days remaining
                  </p>
                  {cooldownStatus.reason && (
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      Reason: {cooldownStatus.reason}
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-lg font-semibold text-green-600 dark:text-green-400 flex items-center gap-2">
                  <FiCheckCircle className="w-5 h-5" />
                  Available
                </div>
              )}
            </div>

            {/* Last Contact */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                Last Contact
              </h3>
              <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {formatDate(memory.last_contacted_at)}
              </div>
              {memory.last_contact_outcome && (
                <div className="flex items-center gap-2 mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {getOutcomeIcon(memory.last_contact_outcome)}
                  <span className="capitalize">{memory.last_contact_outcome.toLowerCase().replace('_', ' ')}</span>
                </div>
              )}
            </div>
          </div>

          {/* Engagement History */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
              Engagement History
            </h3>
            {(memory.total_requests_received || 0) > 0 ? (
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {memory.total_accepted || 0}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Accepted</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {memory.total_declined || 0}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Declined</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-400 dark:text-gray-500">
                    {memory.total_no_response || 0}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">No Response</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                    {memory.total_requests_received || 0}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Total</div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-400 dark:text-gray-500">No outreach attempts recorded yet</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Engagement stats will appear after the first introduction request
                </p>
              </div>
            )}
          </div>

          {/* Do Not Contact Warning */}
          {memory.do_not_contact && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FiAlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                  <div>
                    <p className="font-medium text-red-800 dark:text-red-200">
                      Do Not Contact Flag Set
                    </p>
                    <p className="text-sm text-red-600 dark:text-red-300">
                      Set on {formatDate(memory.do_not_contact_set_at)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleClearDoNotContact}
                  disabled={saving}
                  className="px-3 py-1 text-sm font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
                >
                  Clear Flag
                </button>
              </div>
            </div>
          )}

          {/* Preferences */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Preferences
              </h3>
              {!editingPreferences ? (
                <button
                  onClick={() => setEditingPreferences(true)}
                  className="flex items-center gap-1 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                >
                  <FiEdit2 className="w-4 h-4" />
                  Edit
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditingPreferences(false)}
                    className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSavePreferences}
                    disabled={saving}
                    className="flex items-center gap-1 text-sm text-white bg-indigo-600 hover:bg-indigo-700 px-3 py-1 rounded-lg disabled:opacity-50"
                  >
                    <FiSave className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>

            {editingPreferences ? (
              <div className="space-y-4">
                {/* Preferred Channel */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Preferred Channel
                  </label>
                  <select
                    value={editedPreferences.preferred_contact_channel}
                    onChange={(e) => setEditedPreferences(prev => ({ ...prev, preferred_contact_channel: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  >
                    <option value="email">Email</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="phone">Phone</option>
                  </select>
                </div>

                {/* Preferred Cadence */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Preferred Cadence
                  </label>
                  <select
                    value={editedPreferences.preferred_cadence}
                    onChange={(e) => setEditedPreferences(prev => ({ ...prev, preferred_cadence: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  >
                    <option value="anytime">Anytime</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="biannual">Biannually</option>
                    <option value="annual">Annually</option>
                    <option value="never">Never</option>
                  </select>
                </div>

                {/* Availability Toggles */}
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editedPreferences.open_to_mentorship}
                      onChange={(e) => setEditedPreferences(prev => ({ ...prev, open_to_mentorship: e.target.checked }))}
                      className="w-4 h-4 rounded text-indigo-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Open to Mentorship</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editedPreferences.open_to_board_inquiries}
                      onChange={(e) => setEditedPreferences(prev => ({ ...prev, open_to_board_inquiries: e.target.checked }))}
                      className="w-4 h-4 rounded text-indigo-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Open to Board Inquiries</span>
                  </label>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={editedPreferences.notes}
                    onChange={(e) => setEditedPreferences(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-none"
                    placeholder="Add notes about this executive..."
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  {getChannelIcon(memory.preferred_contact_channel)}
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Preferred Channel: <span className="font-medium capitalize">{memory.preferred_contact_channel || 'Email'}</span>
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <FiCalendar className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Preferred Cadence: <span className="font-medium capitalize">{memory.preferred_cadence || 'Quarterly'}</span>
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  {memory.open_to_mentorship ? (
                    <FiCheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <FiXCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Open to Mentorship
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  {memory.open_to_board_inquiries ? (
                    <FiCheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <FiXCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Open to Board Inquiries
                  </span>
                </div>
                {memory.notes && (
                  <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-600 dark:text-gray-400">{memory.notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Interaction Timeline */}
          {timeline.length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                Interaction Timeline
              </h3>
              <div className="space-y-4">
                {timeline.map((entry, index) => (
                  <div
                    key={entry.id || index}
                    className="flex gap-3 pb-4 border-b border-gray-200 dark:border-gray-700 last:border-0 last:pb-0"
                  >
                    <div className="flex-shrink-0">
                      {getOutcomeIcon(entry.after_state?.outcome || entry.action)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {entry.action?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                      </p>
                      {entry.after_state?.outcome && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Outcome: {entry.after_state.outcome}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {formatDate(entry.created_at)}
                        {entry.actor_id && ` by ${entry.actor_id}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={fetchMemory}
            disabled={loading}
            className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RelationshipMemoryViewer;
